import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Loader2 } from "lucide-react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { toast } from "sonner";
import { isChangeableDay } from "@/lib/appointment-change";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { calendarDay } from "@/lib/appointment-reschedule";
import { changeScheduledDate } from "@/services/appointment-change.service";

interface ChangeScheduledDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentJoin;
  onSuccess?: (updatedAppointment: AppointmentJoin) => void;
  title?: string;
  description?: string;
}

export default function ChangeScheduledDateModal({
  isOpen,
  onClose,
  appointment,
  onSuccess,
  title = "Change Scheduled Date",
  description = "Select a new date for this appointment. The appointment status will remain unchanged.",
}: ChangeScheduledDateModalProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) setSelectedDate(undefined);
  }, [isOpen]);

  const currentMonth = appointment.date ? new Date(`${appointment.date}T00:00:00`) : undefined;

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setLoading(true);
    try {
      const updatedAppointment = await changeScheduledDate(appointment.id, calendarDay(selectedDate));
      toast.success("Scheduled date changed successfully");
      if (onSuccess) {
        onSuccess(updatedAppointment);
      }
      onClose();
    } catch (error) {
      console.error("Failed to change scheduled date:", error);
      toast.error(fieldActionMessage(error, "Failed to change scheduled date"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Calendar
            mode="single"
            selected={selectedDate}
            defaultMonth={selectedDate ?? currentMonth}
            onSelect={setSelectedDate}
            disabled={(date) => !isChangeableDay(date, appointment.date)}
            className="rounded-md border"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!selectedDate || loading}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              "Change Date"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

